import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import {
  Container,
  Col,
  Button,
  Modal,
  Form,
  Spinner,
  Row,
} from "react-bootstrap";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useFinanceApi from "../../services/financeApi";
import { toast } from "react-toastify";
import { GetCurrentUserName } from "../../utils/permissionChecker";
import BasicTable from "../../components/UiComponent/customTable";
const PaymentMethod = () => {
  const { t } = useTranslation();
  const loggedInUser = GetCurrentUserName();
  const { get_payment_method, addPaymentMethod, updatePaymentMethod } =
    useFinanceApi();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [paymentId, setPaymentId] = useState("");
  const [paymentMethodName, setMethodName] = useState("");
  const [description, setDescription] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const RegisterMutation = useMutation(async (newData) => {
    try {
      let result;
      if (isUpdateMode) {
        result = await updatePaymentMethod(newData);
      } else {
        result = await addPaymentMethod(newData);
      }
      if (result) {
        queryClient.invalidateQueries("get_payment_method");
        toast.success(result.message, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        handleCloseModal();
      }
    } catch (error) {
      toast.error(error.message, {
        position: "top-right",
        autoClose: 6000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  });

  const getPaymentQuery = useQuery(["get_payment_method"], () =>
    get_payment_method()
  );
  const dataTest = useMemo(() => {
    return getPaymentQuery.data?.data?.Payment || [];
  }, [getPaymentQuery.data]);
  if (getPaymentQuery.isLoading) {
    return <div>Loading...</div>;
  }
  if (getPaymentQuery.isError) {
    return <div>Error: {getPaymentQuery.error.message}</div>;
  }
  const handleEdit = (event, rowItem) => {
    event.preventDefault();
    setFormError("");
    setMethodName(rowItem.row.original.name);
    setDescription(rowItem.row.original.description);
    setPaymentId(rowItem.row.original.id);
    setIsUpdateMode(true);
    handleShowModal();
  };

  const paymentMethodColumns = [
    {
      header: "Name",
      accessorKey: "name",
    },
    {
      header: "Description",
      accessorKey: "description",
    },
    {
      header: "Action",
      accessor: "id",
      cell: (row) => (
        <div className="d-flex justify-content align-items-center">
          <Button
            variant="btn btn-info btn-sm"
            onClick={(e) => handleEdit(e, row)}
          >
            {t("update")}
          </Button>
        </div>
      ),
    },
  ];

  const handleShowModal = () => {
    setShowModal(true);
  };
  const handleCloseModal = () => {
    setShowModal(false);
    setIsUpdateMode(false);
    setMethodName("");
    setDescription("");
    setPaymentId("");
  };
  const handleButtonSubmit = async (e) => {
    e.preventDefault();
    let formData;
    if (!paymentMethodName || !description) {
      setFormError("Pease fill all fields are required ");
      return;
    }
    if (isUpdateMode) {
      formData = {
        id: paymentId,
        description: description,
        name: paymentMethodName,
        updated_by: loggedInUser,
      };
    } else {
      formData = {
        description: description,
        name: paymentMethodName,
        updated_by: loggedInUser,
        created_by: loggedInUser,
      };
    }
    try {
      setIsSubmitting(true);
      await RegisterMutation.mutate(formData);
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <Container fluid>
      <BasicTable
        data={dataTest}
        columns={paymentMethodColumns}
        title={"Payment Methods"}
        actionButton={
          <Button
            variant="btn btn-info btn-sm m-2"
            onClick={() => handleShowModal()}
          >
            {t("add_paymentMethod")}
          </Button>
        }
      />
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered={true}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title as="h5">
            {isUpdateMode
              ? "Update the payment methods"
              : "Register new payment methods"}{" "}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col lg="12">
                <Form.Group>
                  <Form.Label className="text-secondary">
                    Payment Methods
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Payment Methods"
                    value={paymentMethodName}
                    onChange={(e) => setMethodName(e.target.value)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label className="text-secondary">
                    Description
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    placeholder="Enter description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                  <p className="text-danger"> {formError}</p>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          {isSubmitting ? (
            <Button variant="btn btn-info">
              <Spinner
                as="span"
                animation="grow"
                size="sm"
                role="status"
                aria-hidden="true"
              />
              <span className="visually-hidden">Sending...</span>
            </Button>
          ) : (
            <Button
              variant="btn btn-info"
              onClick={(e) => handleButtonSubmit(e)}
            >
              {isUpdateMode ? "Update" : "Register"}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PaymentMethod;
